import React, { memo, useMemo } from 'react';
import { Layer, Marker, Source } from 'react-map-gl/mapbox';
import type { IGeofencePoint, IResolvedGeofenceItem } from '~/utils/interface/map';
import { CIRCLE_STEPS, EARTH_RADIUS_METERS } from '~/constants/map';

interface GeofenceLayersProps {
  geofences: IResolvedGeofenceItem[];
}

type MapLngLat = [number, number];

const isValidLongitude = (value: number): boolean => value >= -180 && value <= 180;
const isValidLatitude = (value: number): boolean => value >= -90 && value <= 90;

/**
 * Normalizes a geofence point to GeoJSON coordinate order [longitude, latitude].
 *
 * This function accepts input where backend may send either:
 * - correct order: point.lng = longitude, point.lat = latitude
 * - swapped order: point.lng = latitude, point.lat = longitude
 *
 * It validates coordinate ranges and returns null when neither interpretation is valid.
 */
const toMapLngLat = (point: IGeofencePoint): MapLngLat | null => {
  const lng = point.lng;
  const lat = point.lat;

  if (isValidLongitude(lng) && isValidLatitude(lat)) {
    return [lng, lat];
  }

  const swappedLng = point.lat;
  const swappedLat = point.lng;
  if (isValidLongitude(swappedLng) && isValidLatitude(swappedLat)) {
    return [swappedLng, swappedLat];
  }

  return null;
};

const closeLinearRing = (coordinates: MapLngLat[]): MapLngLat[] => {
  if (!coordinates.length) return coordinates;
  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return coordinates;
  return [...coordinates, first];
};

const createCircleRing = (center: IGeofencePoint, radiusInMeters: number, steps = CIRCLE_STEPS): MapLngLat[] => {
  const normalizedCenter = toMapLngLat(center);
  if (!normalizedCenter || radiusInMeters <= 0) return [];

  const [centerLng, centerLat] = normalizedCenter;
  const latRad = (centerLat * Math.PI) / 180;

  const ring: MapLngLat[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const bearing = (i / steps) * Math.PI * 2;
    const dx = radiusInMeters * Math.cos(bearing);
    const dy = radiusInMeters * Math.sin(bearing);

    const lng = centerLng + ((dx / (EARTH_RADIUS_METERS * Math.cos(latRad))) * 180) / Math.PI;
    const lat = centerLat + ((dy / EARTH_RADIUS_METERS) * 180) / Math.PI;

    ring.push([lng, lat]);
  }

  return closeLinearRing(ring);
};

const buildPolygonFeature = (geofence: IResolvedGeofenceItem) => {
  const geometry = geofence.geometry;
  const polygonPoints = geometry.shape === 'polygon' ? geometry.points.map(toMapLngLat).filter(Boolean) : [];
  const coordinates =
    geometry.shape === 'circle'
      ? createCircleRing(geometry.center, geometry.radius)
      : geometry.shape === 'polygon'
        ? closeLinearRing(polygonPoints as MapLngLat[])
        : [];

  if (coordinates.length < 4) return null;

  return {
    type: 'Feature' as const,
    properties: {
      id: geofence.id,
      geofenceId: geofence.geofenceId,
      personId: geofence.personId,
      personName: geofence.personName,
      status: geofence.status,
      color: geofence.style.color,
      fillColor: geofence.style.fillColor,
      fillOpacity: geofence.style.fillOpacity,
      strokeOpacity: geofence.style.opacity,
      strokeWidth: geofence.style.weight,
    },
    geometry: {
      type: 'Polygon' as const,
      coordinates: [coordinates],
    },
  };
};

const GeofenceLayers = ({ geofences }: GeofenceLayersProps) => {
  const polygonGeofences = useMemo(
    () => geofences.filter((g) => g.geometry.shape === 'circle' || g.geometry.shape === 'polygon'),
    [geofences]
  );

  const pointGeofences = useMemo(() => geofences.filter((g) => g.geometry.shape === 'point'), [geofences]);

  const features = useMemo(() => {
    if (!polygonGeofences.length) return [];
    return polygonGeofences
      .map(buildPolygonFeature)
      .filter((feature): feature is NonNullable<ReturnType<typeof buildPolygonFeature>> => feature !== null);
  }, [polygonGeofences]);

  return (
    <>
      {features.map((feature) => {
        const sourceId = `geofence-source-${feature.properties.id}`;
        const fillLayerId = `geofence-fill-${feature.properties.id}`;
        const lineLayerId = `geofence-line-${feature.properties.id}`;

        return (
          <Source
            id={sourceId}
            key={feature.properties.id}
            type='geojson'
            data={{ type: 'FeatureCollection', features: [feature] }}
          >
            <Layer
              id={fillLayerId}
              type='fill'
              paint={{
                'fill-color': feature.properties.fillColor,
                'fill-opacity': feature.properties.fillOpacity,
              }}
            />
            <Layer
              id={lineLayerId}
              type='line'
              paint={{
                'line-color': feature.properties.color,
                'line-opacity': feature.properties.strokeOpacity,
                'line-width': feature.properties.strokeWidth,
              }}
            />
          </Source>
        );
      })}

      {pointGeofences.map((geofence) => {
        const geometry = geofence.geometry as { shape: 'point'; center: IGeofencePoint };
        const normalizedCenter = toMapLngLat(geometry.center);
        if (!normalizedCenter) return null;

        return (
          <Marker
            key={`point-${geofence.id}`}
            longitude={normalizedCenter[0]}
            latitude={normalizedCenter[1]}
            anchor='center'
          >
            <div
              title={geofence.geofenceName}
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: geofence.style.fillColor,
                border: `2px solid ${geofence.style.color}`,
                opacity: 0.9,
              }}
            />
          </Marker>
        );
      })}
    </>
  );
};

export default memo(GeofenceLayers);
